import React, { useEffect, useState, useRef, Component } from 'react';
import { MapContainer, TileLayer, Marker, Circle, Polygon, Popup, Tooltip, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import toast from 'react-hot-toast';
import { MAP_TILES } from '../utils/mapTiles';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// ── Coordinate helpers ──────────────────────────────────────────
const isValidNum = (v) => v !== null && v !== undefined && v !== '' && !isNaN(parseFloat(v));
const hasValidCoords = (lat, lng) =>
  isValidNum(lat) && isValidNum(lng) && parseFloat(lat) !== 0 && parseFloat(lng) !== 0;

// ── Icons ──────────────────────────────────────────────────────
const makeSiteIcon = (name) => L.divIcon({
  html: `<div style="
    background: #0f172a; color: #38bdf8;
    border: 2px solid #38bdf8; border-radius: 8px;
    padding: 3px 8px; font-size: 11px; font-weight: 700;
    white-space: nowrap; box-shadow: 0 4px 12px rgba(0,0,0,0.35);
    display: flex; align-items: center; gap: 4px;
  ">🏢 ${name || 'Site'}</div>`,
  className: '',
  iconSize: [120, 26],
  iconAnchor: [60, 13],
});

const makeCpIcon = (seq, name) => L.divIcon({
  html: `<div style="
    background: #4361ee; color: white;
    border: 2px solid white; border-radius: 50%;
    width: 22px; height: 22px; display: flex;
    align-items: center; justify-content: center;
    font-size: 10px; font-weight: 700;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  ">${seq}</div>`,
  className: '',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

const makePtIcon = (idx) => L.divIcon({
  html: `<div style="
    background: #f59e0b; color: white;
    border: 2px solid white; border-radius: 50%;
    width: 20px; height: 20px; display: flex;
    align-items: center; justify-content: center;
    font-size: 9px; font-weight: 700;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
  ">${idx + 1}</div>`,
  className: '',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// ── Error Boundary to prevent map crashes from blanking page ────
class MapErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Leaflet Map Error caught by Boundary:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: 40, background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: 12, textAlign: 'center', color: '#991b1b', margin: '20px 0'
        }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🗺️⚠️</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Map Render Encountered an Issue</div>
          <div style={{ fontSize: 13, color: '#7f1d1d', margin: '8px 0 16px' }}>
            {this.state.error?.message || 'Invalid coordinates or map layer state'}
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => this.setState({ hasError: false, error: null })}
          >
            🔄 Reset & Reload Map
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ── Locate Me button ──────────────────────────────────────────
function LocateButton() {
  const map = useMap();
  const locate = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not available in browser');
    toast('📡 Getting GPS location...', { duration: 2000 });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        map.flyTo([pos.coords.latitude, pos.coords.longitude], 18, { duration: 1.5 });
        toast.success('Centered to your location');
      },
      () => toast.error('Could not acquire GPS position. Check permissions.')
    );
  };
  return (
    <div style={{ position: 'absolute', bottom: 24, right: 14, zIndex: 1000 }}>
      <button
        type="button"
        onClick={locate}
        title="Go to my current GPS location"
        style={{
          width: 40, height: 40, borderRadius: 8, border: 'none',
          background: 'white', cursor: 'pointer', fontSize: 20,
          boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        🎯
      </button>
    </div>
  );
}

// ── FlyTo component — safely flies when site center changes ───
function FlyToLocation({ siteId, center }) {
  const map = useMap();
  const prevKey = useRef('');

  useEffect(() => {
    if (!center || !isValidNum(center[0]) || !isValidNum(center[1])) return;
    const key = `${siteId || ''}_${parseFloat(center[0]).toFixed(4)}_${parseFloat(center[1]).toFixed(4)}`;
    if (key === prevKey.current) return;
    prevKey.current = key;

    try {
      map.flyTo([parseFloat(center[0]), parseFloat(center[1])], 16, { duration: 1.2 });
    } catch (e) {
      console.warn('Map flyTo failed:', e);
    }
  }, [siteId, center, map]);

  return null;
}

// ── Interactive Map Click Handler for Setting Site Pin & Drawing ──
function MapEventsHandler({ mode, isSettingSitePin, onMapClick, onDrawingClick, onDrawingDblClick }) {
  useMapEvents({
    click(e) {
      if (isSettingSitePin) {
        onMapClick(e.latlng);
        return;
      }
      if (mode) {
        onDrawingClick(e.latlng);
      }
    },
    dblclick(e) {
      if (mode === 'polygon') {
        onDrawingDblClick(e.latlng);
      }
    }
  });
  return null;
}

export default function Geofencing() {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [zones, setZones] = useState([]);
  const [siteCheckpoints, setSiteCheckpoints] = useState([]);
  const [showCheckpoints, setShowCheckpoints] = useState(true);

  // Drawing state
  const [drawMode, setDrawMode] = useState(null); // null | 'circle' | 'polygon'
  const [polyPoints, setPolyPoints] = useState([]);
  const [circleRadius, setCircleRadius] = useState(100);
  const [showForm, setShowForm] = useState(false);
  const [satellite, setSatellite] = useState(true);
  const [isSettingSitePin, setIsSettingSitePin] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'site_boundary',
    alert_on_enter: false,
    alert_on_exit: true,
    radius_meters: 100,
    geojson: null
  });

  // Load all sites
  const loadSites = async () => {
    try {
      const res = await axios.get('/api/sites');
      setSites(res.data);
      if (res.data.length > 0 && !selectedSite) {
        setSelectedSite(res.data[0].id);
      }
    } catch (err) {
      console.error('Error loading sites:', err);
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  // When selectedSite changes: load zones + checkpoints for reference
  useEffect(() => {
    if (!selectedSite) return;

    // Load zones
    axios.get(`/api/geofencing/zones?site_id=${selectedSite}`)
      .then(r => setZones(Array.isArray(r.data) ? r.data : []))
      .catch(() => setZones([]));

    // Load routes & checkpoints for reference
    axios.get('/api/routes')
      .then(async (r) => {
        const siteRoutes = (r.data || []).filter(rt => rt.site_id === selectedSite);
        const allCPs = [];
        await Promise.all(siteRoutes.map(async rt => {
          try {
            const cpRes = await axios.get(`/api/checkpoints/route/${rt.id}`);
            if (Array.isArray(cpRes.data)) {
              allCPs.push(...cpRes.data);
            }
          } catch (_) {}
        }));
        setSiteCheckpoints(allCPs);
      })
      .catch(() => setSiteCheckpoints([]));

    // Reset temporary states
    setDrawMode(null);
    setPolyPoints([]);
    setShowForm(false);
    setIsSettingSitePin(false);
  }, [selectedSite]);

  const currentSite = sites.find(s => s.id === selectedSite);
  const siteHasGPS = currentSite && hasValidCoords(currentSite.latitude, currentSite.longitude);

  // Determine smart map center
  let center = [22.2587, 71.1924]; // Gujarat default
  if (siteHasGPS) {
    center = [parseFloat(currentSite.latitude), parseFloat(currentSite.longitude)];
  } else {
    // If site has no coordinates, check if any checkpoint of this site has GPS
    const cpWithCoords = siteCheckpoints.find(cp => hasValidCoords(cp.latitude, cp.longitude));
    if (cpWithCoords) {
      center = [parseFloat(cpWithCoords.latitude), parseFloat(cpWithCoords.longitude)];
    }
  }

  // ── Handle Drawing ──────────────────────────────────────────
  const handleDrawingClick = (latlng) => {
    if (drawMode === 'circle') {
      const geojson = {
        type: 'Point',
        coordinates: [latlng.lng, latlng.lat],
        properties: { radius: circleRadius }
      };
      setFormData({
        name: `${currentSite?.name || 'Site'} Perimeter`,
        type: 'site_boundary',
        alert_on_enter: false,
        alert_on_exit: true,
        radius_meters: circleRadius,
        geojson
      });
      setDrawMode(null);
      setShowForm(true);
      toast.success('Circle placed! Configure zone details below.');
    } else if (drawMode === 'polygon') {
      setPolyPoints(prev => [...prev, [latlng.lat, latlng.lng]]);
    }
  };

  const handleFinishPolygon = () => {
    if (polyPoints.length < 3) {
      toast.error('At least 3 points are needed to complete a polygon zone');
      return;
    }
    const closed = [...polyPoints, polyPoints[0]];
    const geojson = {
      type: 'Polygon',
      coordinates: [closed.map(p => [p[1], p[0]])]
    };
    setFormData({
      name: `${currentSite?.name || 'Site'} Geofence`,
      type: 'site_boundary',
      alert_on_enter: false,
      alert_on_exit: true,
      radius_meters: null,
      geojson
    });
    setDrawMode(null);
    setPolyPoints([]);
    setShowForm(true);
    toast.success('Polygon completed! Configure zone details below.');
  };

  const handleUndoPoint = () => {
    setPolyPoints(prev => prev.slice(0, -1));
  };

  // ── Set Site Location Pin ────────────────────────────────────
  const handleSetSiteLocation = async (latlng) => {
    if (!currentSite) return;
    try {
      await axios.put(`/api/sites/${currentSite.id}`, {
        latitude: latlng.lat,
        longitude: latlng.lng,
      });
      toast.success(`📍 Center location saved for ${currentSite.name}!`);
      setIsSettingSitePin(false);
      // Update local sites array
      setSites(prev => prev.map(s => s.id === currentSite.id ? { ...s, latitude: latlng.lat, longitude: latlng.lng } : s));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save site location');
    }
  };

  // ── Save Geofence Zone ───────────────────────────────────────
  const handleSaveZone = async () => {
    if (!formData.name?.trim() || !formData.geojson) {
      toast.error('Please enter a zone name and complete drawing');
      return;
    }

    try {
      await axios.post('/api/geofencing/zones', {
        ...formData,
        site_id: selectedSite,
        radius_meters: formData.radius_meters || (formData.geojson?.properties?.radius ?? null)
      });
      toast.success('Geofence zone created successfully!');
      setShowForm(false);
      setFormData({
        name: '',
        type: 'site_boundary',
        alert_on_enter: false,
        alert_on_exit: true,
        radius_meters: 100,
        geojson: null
      });
      // Reload zones
      const r = await axios.get(`/api/geofencing/zones?site_id=${selectedSite}`);
      setZones(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save geofence zone');
    }
  };

  const handleDeleteZone = async (id) => {
    if (!window.confirm('Delete this geofence zone?')) return;
    try {
      await axios.delete(`/api/geofencing/zones/${id}`);
      toast.success('Zone deleted');
      setZones(zones.filter(z => z.id !== id));
    } catch (err) {
      toast.error('Failed to delete zone');
    }
  };

  // ── Safe Zone Renderer ───────────────────────────────────────
  const renderZone = (zone) => {
    try {
      let geo = zone.geojson;
      if (typeof geo === 'string') {
        try { geo = JSON.parse(geo); } catch { return null; }
      }
      if (!geo || typeof geo !== 'object') return null;

      const zoneColor = zone.type === 'restricted' ? '#ef233c' : zone.type === 'route_corridor' ? '#f59e0b' : '#4361ee';

      if (geo.type === 'Point' && Array.isArray(geo.coordinates) && geo.coordinates.length >= 2) {
        const lat = parseFloat(geo.coordinates[1]);
        const lng = parseFloat(geo.coordinates[0]);
        if (isNaN(lat) || isNaN(lng)) return null;

        const radius = parseFloat(zone.radius_meters) || parseFloat(geo.properties?.radius) || 100;

        return (
          <Circle
            key={zone.id}
            center={[lat, lng]}
            radius={radius}
            pathOptions={{ color: zoneColor, fillColor: zoneColor, fillOpacity: 0.18, weight: 2 }}
          >
            <Popup>
              <div style={{ minWidth: 160 }}>
                <strong style={{ fontSize: 13, color: '#0f172a' }}>🛡️ {zone.name}</strong><br />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  Type: <b>{zone.type}</b><br />
                  Radius: <b>{Math.round(radius)}m</b><br />
                  {zone.alert_on_enter ? '🔔 Alert on Enter • ' : ''}
                  {zone.alert_on_exit ? '🚨 Alert on Exit' : ''}
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteZone(zone.id)}
                  style={{
                    marginTop: 8, padding: '4px 10px', background: '#fee2e2',
                    color: '#dc2626', border: 'none', borderRadius: 6,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  🗑️ Delete Zone
                </button>
              </div>
            </Popup>
          </Circle>
        );
      }

      if (geo.type === 'Polygon' && Array.isArray(geo.coordinates) && Array.isArray(geo.coordinates[0])) {
        const positions = geo.coordinates[0]
          .map(c => [parseFloat(c[1]), parseFloat(c[0])])
          .filter(p => !isNaN(p[0]) && !isNaN(p[1]));

        if (positions.length < 3) return null;

        return (
          <Polygon
            key={zone.id}
            positions={positions}
            pathOptions={{ color: zoneColor, fillColor: zoneColor, fillOpacity: 0.18, weight: 2 }}
          >
            <Popup>
              <div style={{ minWidth: 160 }}>
                <strong style={{ fontSize: 13, color: '#0f172a' }}>🛡️ {zone.name}</strong><br />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  Type: <b>{zone.type}</b><br />
                  Points: <b>{positions.length}</b><br />
                  {zone.alert_on_enter ? '🔔 Alert on Enter • ' : ''}
                  {zone.alert_on_exit ? '🚨 Alert on Exit' : ''}
                </div>
                <button
                  type="button"
                  onClick={() => handleDeleteZone(zone.id)}
                  style={{
                    marginTop: 8, padding: '4px 10px', background: '#fee2e2',
                    color: '#dc2626', border: 'none', borderRadius: 6,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  🗑️ Delete Zone
                </button>
              </div>
            </Popup>
          </Polygon>
        );
      }

      return null;
    } catch (err) {
      console.warn('Error rendering zone item:', err);
      return null;
    }
  };

  return (
    <div>
      {/* ── TOP HEADER ────────────────────────────────────────── */}
      <div className="page-header" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div className="page-title">🛡️ Geofencing & Boundary Zones</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            Define perimeters, restricted areas, and corridors with real-time guard breach alerts
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Site Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Site:</span>
            <select
              value={selectedSite || ''}
              onChange={e => setSelectedSite(e.target.value)}
              style={{
                padding: '7px 12px', borderRadius: 8, border: '1.5px solid #cbd5e1',
                fontSize: 13, fontWeight: 600, background: 'white', color: '#0f172a'
              }}
            >
              {sites.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} {!hasValidCoords(s.latitude, s.longitude) ? '(⚠️ No GPS)' : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Satellite / Street Switcher */}
          <button
            type="button"
            onClick={() => setSatellite(!satellite)}
            className="btn btn-sm"
            style={{
              background: satellite ? '#0f172a' : 'white',
              color: satellite ? '#38bdf8' : '#334155',
              border: '1.5px solid #cbd5e1',
              fontWeight: 600
            }}
          >
            {satellite ? '🛰️ Satellite' : '🗺️ Street'}
          </button>

          {/* Toggle Checkpoints */}
          <button
            type="button"
            onClick={() => setShowCheckpoints(!showCheckpoints)}
            className="btn btn-sm"
            style={{
              background: showCheckpoints ? '#e0e7ff' : 'white',
              color: showCheckpoints ? '#4361ee' : '#64748b',
              border: '1.5px solid #cbd5e1'
            }}
            title="Toggle showing route checkpoints on map"
          >
            📍 {showCheckpoints ? 'Hide CPs' : 'Show CPs'}
          </button>

          {/* Drawing Tools */}
          <button
            type="button"
            onClick={() => { setDrawMode(drawMode === 'circle' ? null : 'circle'); setPolyPoints([]); setIsSettingSitePin(false); }}
            className={`btn btn-sm ${drawMode === 'circle' ? 'btn-primary' : ''}`}
            style={{ border: '1.5px solid #cbd5e1' }}
            disabled={!selectedSite}
          >
            ⭕ Draw Circle
          </button>

          <button
            type="button"
            onClick={() => { setDrawMode(drawMode === 'polygon' ? null : 'polygon'); setPolyPoints([]); setIsSettingSitePin(false); }}
            className={`btn btn-sm ${drawMode === 'polygon' ? 'btn-primary' : ''}`}
            style={{ border: '1.5px solid #cbd5e1' }}
            disabled={!selectedSite}
          >
            📐 Draw Polygon
          </button>
        </div>
      </div>

      {/* ── MISSING SITE GPS BANNER ────────────────────────────── */}
      {currentSite && !siteHasGPS && (
        <div style={{
          background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: 10,
          padding: '12px 18px', marginBottom: 14, display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10
        }}>
          <div>
            <span style={{ fontWeight: 700, color: '#92400e', fontSize: 13 }}>
              📍 Site "{currentSite.name}" does not have GPS coordinates configured yet.
            </span>
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
              Click "Set Site Pin" to place its center point on the map, or select a location from your browser GPS.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className={`btn btn-sm ${isSettingSitePin ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setIsSettingSitePin(!isSettingSitePin); setDrawMode(null); }}
            >
              {isSettingSitePin ? '❌ Cancel Pinning' : '📍 Set Site Pin on Map'}
            </button>
          </div>
        </div>
      )}

      {/* ── SETTING PIN ACTIVE BANNER ─────────────────────────── */}
      {isSettingSitePin && (
        <div style={{
          background: '#eff6ff', border: '1.5px solid #93c5fd', borderRadius: 10,
          padding: '10px 16px', marginBottom: 14, color: '#1e40af', fontWeight: 600, fontSize: 13,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span>👉 Click anywhere on the map to set the permanent center point for <strong>{currentSite?.name}</strong>.</span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => setIsSettingSitePin(false)}
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── DRAWING MODE BANNER ────────────────────────────────── */}
      {drawMode === 'circle' && (
        <div style={{
          background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10,
          padding: '12px 16px', marginBottom: 14, display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10
        }}>
          <div style={{ fontSize: 13, color: '#166534', fontWeight: 600 }}>
            ⭕ Circle Mode: Click anywhere on the map to place zone center. Radius:
            <input
              type="number"
              min={20}
              max={2000}
              step={10}
              value={circleRadius}
              onChange={e => setCircleRadius(Math.max(20, parseInt(e.target.value) || 50))}
              style={{
                width: 75, padding: '4px 8px', marginLeft: 8, marginRight: 4,
                borderRadius: 6, border: '1px solid #86efac', fontWeight: 700
              }}
            />
            meters
          </div>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => setDrawMode(null)}
          >
            Cancel
          </button>
        </div>
      )}

      {drawMode === 'polygon' && (
        <div style={{
          background: '#fefce8', border: '1.5px solid #fef08a', borderRadius: 10,
          padding: '12px 16px', marginBottom: 14, display: 'flex',
          justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10
        }}>
          <div style={{ fontSize: 13, color: '#854d0e', fontWeight: 600 }}>
            📐 Polygon Mode: Click points on map ({polyPoints.length} added). Double-click or click button below to finish:
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={polyPoints.length < 3}
              onClick={handleFinishPolygon}
            >
              ✅ Finish Polygon ({polyPoints.length} pts)
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              disabled={polyPoints.length === 0}
              onClick={handleUndoPoint}
            >
              ↩️ Undo Point
            </button>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => { setDrawMode(null); setPolyPoints([]); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── ZONE CONFIGURATION FORM ───────────────────────────── */}
      {showForm && (
        <div className="card" style={{ marginBottom: 16, border: '2px solid #3b82f6' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0, fontSize: 16, color: '#0f172a' }}>⚙️ Configure Geofence Zone</h3>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#64748b' }}
            >
              ✕
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Zone Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g. Main Plant Boundary"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Zone Type</label>
              <select
                value={formData.type}
                onChange={e => setFormData({ ...formData, type: e.target.value })}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
              >
                <option value="site_boundary">Site Boundary (Main Premise)</option>
                <option value="restricted">Restricted Area (No Unauthorized Entry)</option>
                <option value="route_corridor">Route Corridor (Patrol Path)</option>
                <option value="custom">Custom Area</option>
              </select>
            </div>

            {formData.radius_meters && (
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontWeight: 600, fontSize: 13 }}>Radius (meters)</label>
                <input
                  type="number"
                  min={10}
                  max={5000}
                  value={formData.radius_meters}
                  onChange={e => {
                    const r = parseInt(e.target.value) || 50;
                    setFormData({
                      ...formData,
                      radius_meters: r,
                      geojson: formData.geojson ? { ...formData.geojson, properties: { radius: r } } : null
                    });
                  }}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
                />
              </div>
            )}
          </div>

          <div style={{ marginTop: 14, display: 'flex', gap: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.alert_on_enter}
                onChange={e => setFormData({ ...formData, alert_on_enter: e.target.checked })}
              />
              🔔 Alert when Guard enters zone
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.alert_on_exit}
                onChange={e => setFormData({ ...formData, alert_on_exit: e.target.checked })}
              />
              🚨 Alert when Guard exits zone
            </label>
          </div>

          <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <button type="button" onClick={handleSaveZone} className="btn btn-primary">
              💾 Save Geofence Zone
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setFormData({ ...formData, geojson: null }); }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── MAP CONTAINER WITH ERROR BOUNDARY ─────────────────── */}
      <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
        <MapErrorBoundary>
          <MapContainer
            center={center}
            zoom={16}
            maxZoom={22}
            doubleClickZoom={drawMode !== 'polygon'}
            style={{ height: 600, width: '100%', cursor: isSettingSitePin ? 'crosshair' : 'grab' }}
          >
            <TileLayer
              key={satellite ? 'satellite' : 'street'}
              url={satellite ? MAP_TILES.googleHybrid.url : MAP_TILES.googleStreet.url}
              attribution="© Google Maps"
              maxZoom={22}
              maxNativeZoom={21}
            />

            <FlyToLocation siteId={selectedSite} center={center} />
            <LocateButton />

            <MapEventsHandler
              mode={drawMode}
              isSettingSitePin={isSettingSitePin}
              onMapClick={handleSetSiteLocation}
              onDrawingClick={handleDrawingClick}
              onDrawingDblClick={handleFinishPolygon}
            />

            {/* Site Center Marker — ONLY rendered when site has valid numeric coordinates */}
            {siteHasGPS && (
              <Marker
                position={[parseFloat(currentSite.latitude), parseFloat(currentSite.longitude)]}
                icon={makeSiteIcon(currentSite.name)}
              >
                <Popup>
                  <div style={{ minWidth: 160 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>🏢 {currentSite.name}</div>
                    {currentSite.address && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>📍 {currentSite.address}</div>}
                    <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                      Lat: {parseFloat(currentSite.latitude).toFixed(5)}<br />
                      Lng: {parseFloat(currentSite.longitude).toFixed(5)}
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      style={{ marginTop: 8, width: '100%', fontSize: 11 }}
                      onClick={() => setIsSettingSitePin(true)}
                    >
                      📍 Reposition Pin
                    </button>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Existing Geofence Zones */}
            {zones.map(zone => renderZone(zone))}

            {/* Drawing preview for Polygon */}
            {polyPoints.length > 0 && drawMode === 'polygon' && (
              <>
                {polyPoints.map((p, i) => (
                  <Marker key={`pt-${i}`} position={p} icon={makePtIcon(i)} />
                ))}
                {polyPoints.length >= 2 && (
                  <Polygon positions={polyPoints} pathOptions={{ color: '#f59e0b', weight: 2, dashArray: '4 4' }} />
                )}
              </>
            )}

            {/* Reference Route Checkpoints */}
            {showCheckpoints && siteCheckpoints
              .filter(cp => hasValidCoords(cp.latitude, cp.longitude))
              .map(cp => (
                <Marker
                  key={`cp-${cp.id}`}
                  position={[parseFloat(cp.latitude), parseFloat(cp.longitude)]}
                  icon={makeCpIcon(cp.sequence_order || '•', cp.name)}
                >
                  <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                    <span>CP #{cp.sequence_order}: {cp.name}</span>
                  </Tooltip>
                </Marker>
              ))
            }
          </MapContainer>
        </MapErrorBoundary>
      </div>

      {/* ── EXISTING ZONES LIST ───────────────────────────────── */}
      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            🛡️ Defined Zones for {currentSite?.name || 'Selected Site'} ({zones.length})
          </h3>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {zones.filter(z => z.type === 'restricted').length} restricted • {zones.filter(z => z.type === 'site_boundary').length} boundary
          </span>
        </div>

        {zones.length === 0 ? (
          <div style={{ color: '#94a3b8', padding: '24px 0', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⭕</div>
            No geofence zones defined for this site yet.
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Click <b>⭕ Draw Circle</b> or <b>📐 Draw Polygon</b> above to define your perimeter.
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {zones.map(z => (
              <div
                key={z.id}
                style={{
                  padding: '14px 16px', border: '1px solid #e2e8f0', borderRadius: 10,
                  background: 'white', display: 'flex', flexDirection: 'column',
                  justifyContent: 'space-between', gap: 10
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 14, color: '#0f172a' }}>{z.name}</strong>
                    <span className={`badge badge-${z.type === 'restricted' ? 'danger' : z.type === 'route_corridor' ? 'warning' : 'primary'}`} style={{ fontSize: 11 }}>
                      {z.type}
                    </span>
                  </div>

                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {z.alert_on_enter && <span>🔔 Alert on Enter</span>}
                    {z.alert_on_exit && <span>🚨 Alert on Exit</span>}
                    {z.radius_meters && <span>Radius: {Math.round(z.radius_meters)}m</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f1f5f9', paddingTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => handleDeleteZone(z.id)}
                    className="btn btn-sm btn-danger"
                    style={{ fontSize: 11, padding: '3px 10px' }}
                  >
                    🗑️ Delete Zone
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
